import { useEffect, useRef, useState } from 'react';
import { studyAttachments,
         type StudyAttachmentDto, type StudyAttachmentCategory,
         type StudyAttachmentCategoryOption, type OfferAttachmentDto } from '@/api/endpoints';
import { validateUploadSize } from '@/lib/uploadLimits';
import { UploadProgressBar } from '@/components/UploadProgress';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Icon } from '@/components/Icon';

interface Props {
  studyId: number;
  readOnly?: boolean;
}

export function StudyAttachmentsCard({ studyId, readOnly = false }: Props) {
  const [list, setList] = useState<StudyAttachmentDto[] | null>(null);
  // Pliki wgrane przy ofercie, z której powstało opracowanie (zapytanie klienta, karta otworu…).
  const [fromOffer, setFromOffer] = useState<OfferAttachmentDto[]>([]);
  const [categories, setCategories] = useState<StudyAttachmentCategoryOption[]>([]);
  const [pendingCategory, setPendingCategory] = useState<StudyAttachmentCategory>('RESPONSE_AUTHORITY');
  const [pendingDescription, setPendingDescription] = useState('');
  const [pendingReceivedAt, setPendingReceivedAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [uploadPct, setUploadPct] = useState<number | null>(null);   // null = brak wysyłki
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<{ category: StudyAttachmentCategory;
    description: string; receivedAt: string }>({
    category: 'OTHER', description: '', receivedAt: '',
  });
  const inputRef = useRef<HTMLInputElement | null>(null);

  const reload = () => {
    studyAttachments.list(studyId)
      .then(setList)
      .catch(e => setErr(String(e)));
  };

  useEffect(() => {
    reload();
    studyAttachments.categories().then(setCategories).catch(() => setCategories([]));
    studyAttachments.fromOffer(studyId).then(setFromOffer).catch(() => setFromOffer([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyId]);

  async function onFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    const arr = Array.from(files).filter(f => f.size > 0);
    if (arr.length === 0) return;
    // Limit 100 MB na plik — zablokuj, jeśli którykolwiek przekracza
    const sizeErr = validateUploadSize(arr);
    if (sizeErr) { setErr(sizeErr); return; }
    setBusy(true); setErr(null); setInfo(null);
    let added = 0;
    try {
      for (const f of arr) {
        try {
          setUploadPct(0);   // reset paska dla bieżącego pliku
          await studyAttachments.upload(studyId, f, {
            category: pendingCategory,
            description: pendingDescription || undefined,
            receivedAt: pendingReceivedAt || undefined,
          }, p => setUploadPct(p.percent));
          added++;
        } catch (e) {
          setErr(`${f.name}: ${e instanceof Error ? e.message : String(e)}`);
          break;
        }
      }
    } finally {
      setUploadPct(null);
    }
    if (added > 0) {
      setInfo(`Dodano ${added} załącznik${added === 1 ? '' : added < 5 ? 'i' : 'ów'}.`);
      setPendingDescription('');
      setPendingReceivedAt('');
    }
    if (inputRef.current) inputRef.current.value = '';
    setBusy(false);
    reload();
  }

  async function onDelete(attachmentId: number, name: string) {
    if (!confirm(`Odpiąć załącznik "${name}" od opracowania? (Plik pozostanie w archiwum.)`)) return;
    try {
      await studyAttachments.remove(studyId, attachmentId);
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  function startEdit(a: StudyAttachmentDto) {
    setEditingId(a.id);
    setEditDraft({
      category: a.category,
      description: a.description ?? '',
      receivedAt: a.receivedAt ?? '',
    });
  }

  async function saveEdit() {
    if (editingId == null) return;
    try {
      await studyAttachments.updateMetadata(studyId, editingId, {
        category: editDraft.category,
        description: editDraft.description || null,
        receivedAt: editDraft.receivedAt || null,
      });
      setEditingId(null);
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  // Grupowanie po kategorii dla statystyk
  const byCategory: Record<string, number> = {};
  (list ?? []).forEach(a => {
    byCategory[a.category] = (byCategory[a.category] ?? 0) + 1;
  });

  return (
    <div className="card">
      <div className="card-head">
        <div className="card-title">Załączniki opracowania</div>
        <span className="ml-2 text-[11px] text-ink-400">
          {list ? `${list.length} plików` : '…'}
          {Object.keys(byCategory).length > 0 && (
            <> · {Object.entries(byCategory)
                .map(([cat, n]) => {
                  const c = categories.find(c => c.code === cat);
                  return `${c?.displayName ?? cat}: ${n}`;
                })
                .join(' · ')}</>
          )}
        </span>
      </div>
      <div className="card-body !p-0">
        {err && <div className="p-3 text-red-700 bg-red-50 border-b border-red-200 text-[12px]">Błąd: {err}</div>}
        {info && <div className="p-3 text-emerald-700 bg-emerald-50 border-b border-emerald-200 text-[12px]">{info}</div>}

        {!readOnly && (
          <div className="p-4 border-b border-ink-100 bg-ink-50/40">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <label className="field">
                <span className="field-label">Kategoria</span>
                <SearchableSelect
                  value={pendingCategory}
                  onChange={v => setPendingCategory(v as StudyAttachmentCategory)}
                >
                  {categories.map(c => (
                    <option key={c.code} value={c.code}>{c.displayName}</option>
                  ))}
                </SearchableSelect>
              </label>
              <label className="field">
                <span className="field-label">Data otrzymania (opcj.)</span>
                <input
                  type="date"
                  value={pendingReceivedAt}
                  onChange={e => setPendingReceivedAt(e.target.value)}
                  className="input"
                />
              </label>
              <label className="field">
                <span className="field-label">Opis (opcj.)</span>
                <input
                  type="text"
                  value={pendingDescription}
                  onChange={e => setPendingDescription(e.target.value)}
                  placeholder="np. Pismo z ZZ Poznań nr WL.123/2026"
                  className="input"
                />
              </label>
            </div>
            <button
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              className="btn btn-primary btn-sm"
            >
              {busy
                ? <><svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25"/>
                    <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                  </svg> Wgrywam…</>
                : <><Icon name="plus" className="w-3.5 h-3.5"/> Dodaj plik (kategoria + opis powyżej)</>}
            </button>
            <input
              ref={inputRef}
              type="file"
              multiple
              className="hidden"
              onChange={e => onFiles(e.target.files)}
            />
            {uploadPct !== null && (
              <UploadProgressBar percent={uploadPct} label="Wysyłanie pliku…" className="mt-3" />
            )}
          </div>
        )}

        {fromOffer.length > 0 && (
          <div className="p-4 border-b border-ink-100">
            <div className="text-[11px] font-semibold text-ink-500 uppercase tracking-wide mb-2">
              Z oferty {Array.from(new Set(fromOffer.map(a => a.offerNumber).filter(Boolean))).join(', ')}
            </div>
            <div className="flex flex-col gap-1.5">
              {fromOffer.map(a => (
                <div key={a.id} className="flex items-center gap-2 text-[12px]">
                  <Icon name="file-text" className="w-3.5 h-3.5 text-ink-400 shrink-0"/>
                  <a href={a.downloadUrl} target="_blank" rel="noreferrer"
                     className="text-water-700 hover:text-water-900 truncate max-w-[320px]" title={a.fileName ?? ''}>
                    {a.fileName ?? 'plik'}
                  </a>
                  <span className="pill pill-earth text-[10px]">{a.categoryName}</span>
                  {(a.boreholeDesignation || a.intakeName) && (
                    <span className="text-[11px] text-ink-400">
                      {a.boreholeDesignation ? `otwór ${a.boreholeDesignation}` : `ujęcie ${a.intakeName}`}
                    </span>
                  )}
                  {a.description && <span className="text-[11px] text-ink-500 truncate" title={a.description}>— {a.description}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {!list && <div className="p-4 text-ink-400 text-[12px]">Ładowanie…</div>}
        {list && list.length === 0 && (
          <div className="p-6 text-center text-[12px] text-ink-400">
            <Icon name="file-text" className="w-8 h-8 mx-auto mb-2 text-ink-300"/>
            Brak załączników. Tu trafiają pisma z urzędu, decyzje, korespondencja —
            wszystko z dokumentów otrzymanych w trakcie procedowania opracowania.
          </div>
        )}
        {list && list.length > 0 && (
          <table className="table-hw">
            <thead>
              <tr>
                <th>Nazwa pliku</th>
                <th>Kategoria</th>
                <th>Opis</th>
                <th>Data otrzymania</th>
                <th>Dodany</th>
                <th>Autor</th>
                <th className="text-right">Akcje</th>
              </tr>
            </thead>
            <tbody>
              {list.map(a => {
                const isEdit = editingId === a.id;
                return (
                  <tr key={a.id}>
                    <td>
                      <div className="font-semibold text-ink-900 text-[13px] max-w-[280px] truncate"
                           title={a.filename ?? ''}>{a.filename ?? '—'}</div>
                      <div className="font-mono text-[10px] text-ink-400">
                        {a.sizeBytes != null
                          ? a.sizeBytes < 1024 ? `${a.sizeBytes} B`
                          : a.sizeBytes < 1024 * 1024 ? `${(a.sizeBytes / 1024).toFixed(1)} KB`
                          : `${(a.sizeBytes / 1024 / 1024).toFixed(1)} MB`
                          : ''}
                      </div>
                    </td>
                    <td>
                      {isEdit ? (
                        <SearchableSelect
                          value={editDraft.category}
                          onChange={v => setEditDraft(d => ({ ...d, category: v as StudyAttachmentCategory }))}
                          className="select-sm text-[11px]"
                        >
                          {categories.map(c => (
                            <option key={c.code} value={c.code}>{c.displayName}</option>
                          ))}
                        </SearchableSelect>
                      ) : (
                        <span className={'pill ' + categoryPillClass(a.category)}>
                          {a.categoryName}
                        </span>
                      )}
                    </td>
                    <td className="text-[12px]">
                      {isEdit ? (
                        <input
                          value={editDraft.description}
                          onChange={e => setEditDraft(d => ({ ...d, description: e.target.value }))}
                          className="input input-sm text-[12px]"
                        />
                      ) : (a.description
                          ? <span title={a.description}>{a.description.length > 60 ? a.description.slice(0, 60) + '…' : a.description}</span>
                          : <span className="text-ink-400 italic">—</span>)}
                    </td>
                    <td className="text-[11px] font-mono">
                      {isEdit ? (
                        <input
                          type="date"
                          value={editDraft.receivedAt}
                          onChange={e => setEditDraft(d => ({ ...d, receivedAt: e.target.value }))}
                          className="input input-sm text-[11px]"
                        />
                      ) : (a.receivedAt ?? <span className="text-ink-400">—</span>)}
                    </td>
                    <td className="text-[11px] text-ink-500 font-mono">
                      {a.createdAt ? new Date(a.createdAt).toLocaleDateString('pl-PL') : '—'}
                    </td>
                    <td className="text-[11px] text-ink-600">{a.createdByName ?? '—'}</td>
                    <td className="text-right whitespace-nowrap">
                      {a.archiveDocumentId && (
                        <a href={`/api/archive/documents/${a.archiveDocumentId}/download`}
                           target="_blank" rel="noreferrer"
                           className="text-water-700 hover:text-water-900 px-1" title="Pobierz">
                          <Icon name="download" className="w-3.5 h-3.5 inline"/>
                        </a>
                      )}
                      {!readOnly && (
                        isEdit ? (
                          <>
                            <button onClick={saveEdit}
                                    className="text-emerald-600 hover:text-emerald-800 px-1"
                                    title="Zapisz">
                              <Icon name="check" className="w-3.5 h-3.5"/>
                            </button>
                            <button onClick={() => setEditingId(null)}
                                    className="text-ink-400 hover:text-ink-700 px-1"
                                    title="Anuluj">
                              <Icon name="x" className="w-3.5 h-3.5"/>
                            </button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => startEdit(a)}
                                    className="text-ink-500 hover:text-water-700 px-1"
                                    title="Edytuj kategorię/opis">
                              <Icon name="edit" className="w-3.5 h-3.5"/>
                            </button>
                            <button onClick={() => onDelete(a.id, a.filename ?? '')}
                                    className="text-ink-400 hover:text-red-600 px-1"
                                    title="Odepnij od opracowania (plik zostanie w archiwum)">
                              <Icon name="x" className="w-3.5 h-3.5"/>
                            </button>
                          </>
                        )
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function categoryPillClass(c: StudyAttachmentCategory): string {
  switch (c) {
    case 'RESPONSE_AUTHORITY': return 'pill-water';
    case 'DECISION': return 'pill-green';
    case 'REJECTION': return 'pill-amber';
    case 'CORRESPONDENCE': return 'pill-earth';
    case 'OTHER':
    default: return '';
  }
}
